import { useFormikContext } from 'formik';
import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { arrayToObject } from '../../../../../../helpers/array-to-tree-helper';
import { useStore } from '../../../../../../contexts/store-provider';
import { IProductFiles } from '../../../../../../models/response/IProductResponse';
import { formatMessage } from '../../../../../../translations/format-message';
import toast from 'react-hot-toast';
import { ChecklistItem } from '../../../../../../models/forms/IAddUpdateProduct';
import { Button } from 'react-bootstrap';
import {
  extractSelectedColumnData,
  filterTableDataBySelected,
} from '../../../../../../helpers/column-utils';
import {
  normalizeDimensionsByColumn,
  transformDimensionData,
} from '../../../../../../helpers/array-to-tree-helper';

interface IProps {
  selectedProcess: number;
  id: number;
  selectedImage: IProductFiles[];
  selectedImageIndex: number;
}
const DimensionTable = (props: IProps) => {
  const { selectedProcess, id } = props;
  const { values, setFieldValue } = useFormikContext<any>();
  const { productStore, teplarPrintStore } = useStore();
  const {
    getDimensionTableColumnKeys,
    getDimensionByProcessId,
    getDimensionOrder,
  } = productStore;

  const dimensionTableData = values?.Dimension;

  // 1) Add this useMemo near the top (after you have access to `values` and `selectedProcess`):
  const selectedProcessIndex = useMemo(
    () =>
      values?.Process?.findIndex(
        (p: any) => Number(p?.ProcessId) === Number(selectedProcess)
      ),
    [values?.Process, selectedProcess]
  );

  // 2) Replace your existing useLayoutEffect with this (uses selectedProcessIndex and guards):

  // 3) Update tableData and selectedColumns useMemo to use the index:
  const fallbackDimensionRows = useMemo(() => {
    const process = values?.Process?.[selectedProcessIndex];
    const dimensionTab = process?.fileIndices?.find((fi: any) => {
      const isDimension =
        fi?.IsDimension === true || String(fi?.IsDimension).toLowerCase() === 'true';
      return isDimension && Array.isArray(fi?.DimensionsData) && fi.DimensionsData.length > 0;
    });
    return Array.isArray(dimensionTab?.DimensionsData) ? dimensionTab.DimensionsData : [];
  }, [selectedProcessIndex, values]);

  const fallbackTableData = useMemo(
    () => transformDimensionData(normalizeDimensionsByColumn(fallbackDimensionRows)),
    [fallbackDimensionRows]
  );

  const tableData = useMemo(() => {
    const processTableData =
      values?.Process?.[selectedProcessIndex]?.DimensionDetails?.TableData || [];
    const processColumns =
      values?.Process?.[selectedProcessIndex]?.DimensionDetails?.SelectedColumns || [];

    if (processTableData.length > 0) return processTableData;
    if (processColumns.length > 0) return [];

    return fallbackTableData;
  }, [selectedProcessIndex, values, fallbackTableData]);

  const selectedColumns = useMemo(() => {
    const getColumnOrder = () => {
      const masterOrder = Array.isArray(getDimensionTableColumnKeys)
        ? getDimensionTableColumnKeys
          .map((col: any) => Number(col?.Id || 0))
          .filter((dimensionId: number) => dimensionId > 0)
        : [];

      return masterOrder.length > 0 ? masterOrder : getDimensionOrder || [];
    };

    const processColumns =
      values?.Process?.[selectedProcessIndex]?.DimensionDetails?.SelectedColumns || [];
    const fallbackColumns =
      normalizeDimensionsByColumn(fallbackDimensionRows).map((dd: any) => ({
        id: Number(dd?.DimensionId || -1),
        value: Number(dd?.DimensionId || -1),
        label: dd?.DimensionColumn || '',
        disabled: false,
        data: {
          Id: Number(dd?.DimensionId || -1),
          Messages: dd?.DimensionColumn || '',
          MessagesType: dd?.DimensionType || 0,
        },
      })) || [];
    const columns = processColumns.length > 0 ? processColumns : fallbackColumns;

    const uniqueLabels = (list: any[]) => {
      const seen = new Set<string>();
      return (
        list
          ?.map((e: any) => e?.label)
          ?.filter((label: string) => {
            const key = String(label || '').trim().toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }) || []
      );
    };

    const order = getColumnOrder();
    if (!order || order.length === 0)
      return uniqueLabels(columns);

    return (
      uniqueLabels(
        [...columns]
        .sort((a: any, b: any) => {
          const indexA = order.indexOf(Number(a.id));
          const indexB = order.indexOf(Number(b.id));

          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
      )
    );
  }, [
    selectedProcessIndex,
    values,
    getDimensionOrder,
    getDimensionTableColumnKeys,
    fallbackDimensionRows,
  ]);
  // const keys = tableData[0] ? Object.keys(tableData[0]) : [];
  // const headers = keys?.length > 0 ? [...keys.map((e) => e)] : [];

  // const keyDiff = headers.length - selectedColumns.length;

  const handleTextChange = (index: number, text: string) => {
    // const updatedData = [...checklistData];
    // updatedData[index].CheckMessage = text;
    // setChecklistData(updatedData);
    setFieldValue(
      `Process.${selectedProcess}.fileIndices.${props.selectedImageIndex - 1}.CheckItems.${index}.CheckMessage`,
      text
    );
  };

  const handleDeleteItem = (index: number) => {
    const updatedData = values.Process[selectedProcess].fileIndices[
      props.selectedImageIndex - 1
    ].CheckItems.filter((_, i) => i !== index);
    setFieldValue(
      `Process.${selectedProcess}.fileIndices.${props.selectedImageIndex - 1}.CheckItems`,
      updatedData
    );
  };

  const handleAddItem = () => {
    const newItem: ChecklistItem = {
      CheckMessage: '',
      IsChecked: false,
      IsDisabled: false,
    };
    const procIdx = selectedProcess;
    const imgIdx = props.selectedImageIndex - 1;
    // guard: require valid indices
    if (procIdx < 0 || imgIdx < 0) return;

    // safely read existing items (fallback to empty array)
    const existingItems =
      values?.Process?.[procIdx]?.fileIndices?.[imgIdx]?.CheckItems ?? [];

    const updatedItems = [...existingItems, newItem];

    // update formik path and local state immutably
    if (values?.Process?.[procIdx]?.fileIndices?.[imgIdx]) {
      setFieldValue(
        `Process.${procIdx}.fileIndices.${imgIdx}.CheckItems`,
        updatedItems
      );
    } else {
      toast.error(formatMessage('select_image_checklist_items'));
    }
  };

  const getDisplayLines = (value: any): string[] => {
    if (value == null) return [];

    if (Array.isArray(value)) {
      return value.map((item) => String(item));
    }

    const text = String(value);
    if (text.length === 0) return [];

    // Preserve comma-separated dimension values on a single line.
    // Only explicit line breaks should render as multiple lines in step 4.
    if (text.includes('\n')) {
      return text.split('\n');
    }

    // For numeric sequences entered with spaces (e.g. "4 33 88"), show one per line.
    const trimmedText = text.trim();
    const isNumericSequence = /^-?\d+(\.\d+)?(\s+-?\d+(\.\d+)?)+$/.test(trimmedText);
    if (isNumericSequence) {
      return trimmedText.split(/\s+/);
    }

    return [text];
  };

  return (
    <div className="flex-column d-flex mt-3 step4Dimension">
      <div className="tableDesign dimension-table flex-fill border">
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            <table className="table table-hover table-bordered">
              <thead>
                <tr>
                  {selectedColumns?.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData?.map((row: any, rowIndex: number) => (
                  <tr
                    key={rowIndex}
                    className=""
                  >
                    {selectedColumns?.map((header, colIndex) => {
                      const value = row?.[header];
                      const lines = getDisplayLines(value);

                      return (
                        <td
                          className="p-2"
                          key={colIndex}
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {lines.length === 0
                            ? formatMessage('-')
                            : lines.map((line, idx) => (
                              <div key={idx} className='d-block p-1' style={{ minHeight: '1.2em' }}>{line || '\u00A0'}</div>
                            ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {(tableData?.length === 0 ||
                  Object.keys(tableData[0])?.length === 0) && (
                  <tr className="">
                    <td
                      colSpan={
                        (
                          values?.Process?.[selectedProcessIndex]
                            ?.DimensionDetails?.SelectedColumns || []
                        )?.length + 1
                      }
                      className=""
                    >
                      No data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DimensionTable;
